import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getGift } from '../../services/giftService';
import { useAuth } from '../../hooks/useAuth';
import { useToast } from '../../hooks/useToast';
import styles from './GiftAccountCreatePage.module.css';

// Account-creation page that completes a gifted-membership redemption (public, no auth).
const initialForm = {
  firstName: '',
  lastName: '',
  email: '',
  password: '',
  confirmPassword: ''
};

const validateForm = ({ firstName, lastName, email, password, confirmPassword }) => {
  const errors = {};
  if (!firstName.trim()) errors.firstName = 'First name is required';
  if (!lastName.trim()) errors.lastName = 'Last name is required';
  if (!email.trim()) errors.email = 'Email is required';
  else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) errors.email = 'Enter a valid email';
  if (!password) errors.password = 'Password is required';
  else if (password.length < 8) errors.password = 'Password must be at least 8 characters';
  if (confirmPassword !== password) errors.confirmPassword = 'Passwords do not match';
  return errors;
};

const GiftAccountCreatePage = () => {
  const { code } = useParams();
  const navigate = useNavigate();
  const { register, login } = useAuth();
  const { toast } = useToast();

  const [loading, setLoading] = useState(true);
  const [gift, setGift] = useState(null);
  const [invalid, setInvalid] = useState(false);
  const [loadError, setLoadError] = useState(false);
  const [reloadToken, setReloadToken] = useState(0);
  const [form, setForm] = useState(initialForm);
  const [fieldErrors, setFieldErrors] = useState({});
  const [formError, setFormError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    let active = true;
    const load = async () => {
      try {
        const { gift: data } = await getGift(code);
        if (active) setGift(data);
      } catch (error) {
        // A 404 is the only answer that means this code is not a gift. Every other
        // failure is ours, and this is the step that CREATES the account - telling a
        // recipient their paid gift is invalid here is the worst place to lie. Same
        // split as the sibling redeem page (card 53615), and as this file already
        // does for the submit path below.
        if (active) {
          if (error?.status === 404) setInvalid(true);
          else setLoadError(true);
        }
      } finally {
        if (active) setLoading(false);
      }
    };
    load();
    return () => {
      active = false;
    };
  }, [code, reloadToken]);

  const handleChange = useCallback((field) => (e) => {
    const { value } = e.target;
    setForm((prev) => ({ ...prev, [field]: value }));
    setFieldErrors((prev) => ({ ...prev, [field]: undefined }));
    setFormError('');
  }, []);

  const handleSubmit = useCallback(
    async (e) => {
      e.preventDefault();
      const errors = validateForm(form);
      if (Object.keys(errors).length > 0) {
        setFieldErrors(errors);
        return;
      }

      setSubmitting(true);
      setFormError('');
      try {
        const response = await register({
          firstName: form.firstName.trim(),
          lastName: form.lastName.trim(),
          email: form.email.trim(),
          password: form.password,
          confirmPassword: form.confirmPassword,
          giftCode: code
        });

        // Supabase-auth register returns no token — log in to establish the session.
        if (!response?.token) {
          await login(form.email.trim(), form.password);
        }

        toast.success('Your complimentary membership is active. Welcome to Oval.');
        navigate('/dashboard');
      } catch (err) {
        const message =
          err?.errors?.[0] || err?.message || 'Something went wrong. Please try again.';
        setFormError(message);
        toast.error(message);
        setSubmitting(false);
      }
    },
    [form, code, register, login, toast, navigate]
  );

  const renderStateMessage = (title, subtitle) => (
    <div className={styles.stateCard}>
      <div className={styles.stateMark} />
      <h1 className={styles.stateTitle}>{title}</h1>
      <p className={styles.stateSubtitle}>{subtitle}</p>
    </div>
  );

  const renderField = (field, label, type = 'text', autoComplete) => (
    <div className={styles.fieldGroup}>
      <label className={styles.label} htmlFor={field}>
        {label}
      </label>
      <input
        id={field}
        className={`${styles.input} ${fieldErrors[field] ? styles.inputError : ''}`}
        type={type}
        value={form[field]}
        onChange={handleChange(field)}
        autoComplete={autoComplete}
        disabled={submitting}
      />
      {fieldErrors[field] && <span className={styles.fieldError}>{fieldErrors[field]}</span>}
    </div>
  );

  const handleRetry = useCallback(() => {
    setLoading(true);
    setInvalid(false);
    setLoadError(false);
    setReloadToken((token) => token + 1);
  }, []);

  const isClaimable =
    !loading && !invalid && !loadError && gift?.status === 'active';

  return (
    <div className={styles.page}>
      <div className={styles.glow} />
      <div className={styles.content}>
        {loading && (
          <div className={styles.stateCard}>
            <div className={styles.spinner} />
            <p className={styles.stateSubtitle}>Preparing your gift…</p>
          </div>
        )}

        {!loading && loadError && (
          <div className={styles.stateCard}>
            <div className={styles.stateMark} />
            <h1 className={styles.stateTitle}>We couldn&rsquo;t load your gift</h1>
            <p className={styles.stateSubtitle}>
              Something went wrong on our end &mdash; your gift is safe. Please try again.
            </p>
            <button className={styles.submit} onClick={handleRetry} type="button">
              Try again
            </button>
          </div>
        )}

        {!loading && !loadError && invalid &&
          renderStateMessage(
            'This gift link is no longer valid',
            'The link may be incorrect or has expired. Please check with the person who shared it.'
          )}

        {!loading && !invalid && gift?.status === 'redeemed' &&
          renderStateMessage(
            'This gift has already been claimed',
            'This complimentary membership has been redeemed and is no longer available.'
          )}

        {!loading && !invalid && gift?.status === 'void' &&
          renderStateMessage(
            'This gift link is no longer valid',
            'This gift was withdrawn and can no longer be claimed.'
          )}

        {isClaimable && (
          <div className={styles.card}>
            <p className={styles.eyebrow}>Claim your gift</p>
            <h1 className={styles.title}>
              A complimentary {gift.tierName} membership
            </h1>
            <p className={styles.subtitle}>
              {gift.durationMonths} {gift.durationMonths === 1 ? 'month' : 'months'} of Oval, on the
              house. Create your account to activate it.
            </p>

            <form className={styles.form} onSubmit={handleSubmit} noValidate>
              <div className={styles.row}>
                {renderField('firstName', 'First name', 'text', 'given-name')}
                {renderField('lastName', 'Last name', 'text', 'family-name')}
              </div>
              {renderField('email', 'Email', 'email', 'email')}
              {renderField('password', 'Password', 'password', 'new-password')}
              {renderField('confirmPassword', 'Confirm password', 'password', 'new-password')}

              {formError && <div className={styles.formError}>{formError}</div>}

              <button className={styles.submit} type="submit" disabled={submitting}>
                {submitting ? 'Claiming your gift…' : 'Claim membership'}
              </button>
            </form>
          </div>
        )}
      </div>
    </div>
  );
};

export default GiftAccountCreatePage;
